import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Package, AlertCircle } from 'lucide-react';
import { StaffProductsHeader } from './StaffProductsHeader';
import { StaffProductsList } from './StaffProductsList';
import { useOptimizedProductList } from '@/hooks/useOptimizedProductList';
import { Order } from '@/hooks/useOrdersManagement/types';
import { FastTrackProductEntry } from '@/components/FastTrackProductEntry';
import { useToast } from '@/hooks/use-toast';

export const StaffOrderProductsView = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [showFastTrack, setShowFastTrack] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch order details
  const { data: order, isLoading: orderLoading, error: orderError } = useQuery({
    queryKey: ['order', orderId],
    queryFn: async (): Promise<Order> => {
      if (!orderId) throw new Error('Order ID is required');
      
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .single();
      
      if (error) throw error;
      
      return {
        id: data.id,
        supplierName: data.supplier_name,
        deliveryDate: data.delivery_date,
        status: data.status as "active" | "completed" | "archived",
        productCount: data.product_count || 0,
        hasCredit: data.has_credit || false,
        documents: data.documents || [],
        createdBy: data.created_by
      };
    },
    enabled: !!orderId
  });

  // Fetch products using optimized hook
  const { 
    products, 
    productsLoading, 
    productsError,
    totalProducts,
    isLargeDataset
  } = useOptimizedProductList(orderId || '', !!orderId);

  const handleAddProduct = async (product: {
    barcode: string;
    name: string;
    boxes: number;
    unitsPerBox: number;
    photos: string[];
    creationSource: "scan";
  }): Promise<boolean> => {
    try {
      if (!orderId) throw new Error('Order ID is required');

      const payload: any = {
        order_id: orderId,
        barcode: product.barcode,
        name: product.name,
        boxes: product.boxes,
        units_per_box: product.unitsPerBox,
        photos: product.photos,
        creation_source: product.creationSource
      };

      const { error } = await supabase
        .from('products')
        .insert(payload);

      if (error) throw error;

      // Refresh the products list
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });

      toast({
        title: "Product Added",
        description: `${product.name} has been added to the order.`,
        className: "bg-green-50 border-green-200 text-green-800"
      });

      setShowFastTrack(false);
      return true;
    } catch (error: any) {
      console.error('Error adding product:', error);
      toast({
        title: "Error Adding Product",
        description: error.message || "Failed to add product. Please try again.",
        variant: "destructive"
      });
      return false;
    }
  };

  // Loading state
  if (orderLoading || productsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <Package className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {orderLoading ? 'Loading Order...' : 'Loading Products...'}
          </h2>
          <p className="text-gray-600">
            Please wait while we fetch the order details.
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (orderError || productsError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Order</h2>
          <p className="text-gray-600 mb-4">
            {orderError?.message || productsError?.message || 'Unable to load order details.'}
          </p>
          <div className="space-y-2">
            <button 
              onClick={() => window.location.reload()} 
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Refresh Page
            </button>
            <button 
              onClick={() => window.history.back()}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Order not found
  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-4">
            The order you're looking for doesn't exist or has been removed.
          </p>
          <button 
            onClick={() => window.history.back()}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="w-full px-2 md:px-4 lg:container lg:mx-auto py-2 md:py-6">
        {/* Header */}
        <StaffProductsHeader order={order} onQuickAdd={() => setShowFastTrack(true)} />

        {/* Large dataset warning */}
        {isLargeDataset && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 dark:bg-yellow-900/20 dark:border-yellow-800">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Large Dataset Detected</h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                  This order has {totalProducts} products. Performance optimizations are active.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Products List */}
        {products.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="text-center py-8">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No Products Found</h3>
              <p className="text-sm text-gray-500">
                This order doesn't have any products scanned yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <StaffProductsList products={products} />
        )}
      </div>

      {/* FastTrack Product Entry */}
      <FastTrackProductEntry
        isOpen={showFastTrack}
        onClose={() => setShowFastTrack(false)}
        onAddProduct={handleAddProduct}
      />
    </div>
  );
};